import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Why this doesn't throw at import time:
 *
 * The obvious move is to throw when the env vars are missing. But this module is
 * imported by everything, so a throw here takes the whole page down with a blank
 * screen — including parts that need no credentials. Instead we record the
 * problem, build a client against an unreachable placeholder, and let callers
 * surface `configError` in the UI. Network calls then fail individually rather
 * than the app failing to boot.
 *
 * The failure this most often guards against is subtle: Vite inlines env vars at
 * BUILD time, so a Netlify deploy created before the variables were set has
 * `undefined` permanently baked into its bundle. Adding the vars afterwards
 * changes nothing until a fresh deploy.
 */
export const configError: string | null =
  !url || !key
    ? [
        `Missing ${!url ? "VITE_SUPABASE_URL" : ""}${!url && !key ? " and " : ""}${!key ? "VITE_SUPABASE_ANON_KEY" : ""}.`,
        "",
        "Local:   copy .env.example to .env and fill both in from",
        "         Supabase → Project Settings → API, then restart the dev server.",
        "Netlify: add them under Site configuration → Environment variables,",
        "         then trigger a NEW deploy — Vite inlines them at build time,",
        "         so an existing build will not pick them up.",
      ].join("\n")
    : null;

export const isConfigured = configError === null;

export const supa = createClient(
  url || "https://unconfigured.invalid",
  key || "unconfigured",
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // We sign in with 6-digit email codes, never magic links, so there is no
      // token to parse out of the URL. Leaving this on makes supabase-js inspect
      // and rewrite the address bar on every load for no reason.
      detectSessionInUrl: false,
    },
  },
);
